import React, { useState, useEffect, useRef } from 'react';
import { Github, Linkedin, Instagram, Menu, X, Mail, Moon, Sun, Download } from 'lucide-react';
import { useSectionNavigation } from '../hooks/useSectionNavigation';

interface SidebarProps {
  activeSection: string;
  setActiveSection: (section: string) => void;
  onOpenPalette: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ activeSection, setActiveSection, onOpenPalette }) => {
  const navigateToSection = useSectionNavigation(setActiveSection);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [terminalPath, setTerminalPath] = useState('~/portfolio/projects');
  const [displayedPath, setDisplayedPath] = useState('~/portfolio/projects');
  const [isTyping, setIsTyping] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(true);

  useEffect(() => {
    // Apply theme on mount and when it changes
    if (isDarkMode) {
      document.documentElement.classList.remove('light');
      document.documentElement.classList.add('dark');
      document.body.classList.remove('light');
    } else {
      document.documentElement.classList.remove('dark');
      document.documentElement.classList.add('light');
      document.body.classList.add('light');
    }
  }, [isDarkMode]);

  // Driven by activeSection rather than by the click handler, so the path also
  // keeps up when the command palette changes sections.
  const isInitialPath = useRef(true);
  useEffect(() => {
    const pathMap: { [key: string]: string } = {
      'projects': '~/portfolio/projects',
      'experience': '~/portfolio/experience',
      'contact': '~/portfolio/contact',
      'funhouse': '~/portfolio/funhouse',
    };
    const newPath = pathMap[activeSection] || '~/portfolio';

    // Don't animate the path we already render on first paint
    if (isInitialPath.current) {
      isInitialPath.current = false;
      setTerminalPath(newPath);
      setDisplayedPath(newPath);
      return;
    }

    setIsTyping(true);
    setDisplayedPath('');

    // Type out the new path character by character
    let currentIndex = 0;
    const typingSpeed = 50; // milliseconds per character

    const typeInterval = setInterval(() => {
      if (currentIndex < newPath.length) {
        setDisplayedPath(newPath.slice(0, currentIndex + 1));
        currentIndex++;
      } else {
        clearInterval(typeInterval);
        setIsTyping(false);
        setTerminalPath(newPath);
      }
    }, typingSpeed);

    return () => clearInterval(typeInterval);
  }, [activeSection]);

  const handleNavigation = (sectionId: string) => {
    navigateToSection(sectionId, () => {
      // Close menu first on mobile
      if (window.innerWidth < 1024) {
        setIsMenuOpen(false);
      }
    });
  };

  const toggleTheme = () => {
    setIsDarkMode(!isDarkMode);
  };

  return (
    <>
      {/* Mobile Menu Button */}
      <button 
        onClick={() => setIsMenuOpen(!isMenuOpen)}
        className="fixed top-4 right-4 p-2.5 rounded-lg bg-dev-bg-card border border-dev-border text-dev-text-muted hover:text-dev-text z-50 lg:hidden shadow-lg"
      >
        {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
      </button>

      {/* Sidebar Content */}
      <div className={`
        fixed inset-0 w-full h-full min-h-screen backdrop-blur-md
        lg:w-auto lg:h-full lg:relative lg:bg-transparent lg:inset-auto lg:backdrop-blur-none
        transition-all duration-300 ease-in-out z-40 lg:z-auto
        ${isMenuOpen ? 'top-0 left-0 visible' : 'invisible -translate-y-full lg:visible lg:translate-y-0 lg:top-0'}
        ${isDarkMode ? 'bg-dev-bg/95' : 'bg-white/95'}
      `}>
        <div className="flex flex-col h-full overflow-y-auto lg:overflow-visible" style={{ padding: window.innerWidth >= 1024 ? 'var(--sidebar-padding-vertical) var(--sidebar-padding) calc(var(--sidebar-padding-vertical) * 0.5)' : '2rem 2rem' }}>
          {/* Top Section */}
          <div className="flex-1 min-h-0 lg:overflow-y-auto">
            {/* Profile section */}
            <div className="mb-8 lg:mb-8 animate-fade-in" style={{ marginBottom: window.innerWidth >= 1024 ? 'var(--vertical-spacing)' : undefined }}>
              {/* Mobile: Centered vertical layout */}
              <div className="flex lg:hidden flex-col items-center text-center mb-6">
                <img 
                  src="/images/profile-photo.webp"
                  alt="Parth Kohale" 
                  className="w-20 h-20 rounded-full border-2 border-dev-accent shadow-glow mb-4 object-cover"
                  loading="eager"
                />
                <h1 className="text-2xl font-bold font-mono text-dev-text mb-1">
                  Parth Kohale
                </h1>
                <h2 className="text-dev-text-muted text-sm font-mono">
                  AI Engineer @ Deriv
                </h2>
              </div>
              
              {/* Desktop: Horizontal layout */}
              <div className="hidden lg:flex items-center justify-between gap-3 lg:gap-4 mb-6" style={{ marginBottom: window.innerWidth >= 1024 ? 'calc(var(--vertical-spacing) * 0.75)' : undefined }}>
                <div>
                  <h1 className="text-2xl lg:text-3xl xl:text-4xl font-bold font-mono text-dev-text">
                    Parth Kohale
                  </h1>
                  <h2 className="text-dev-text-muted text-sm lg:text-base xl:text-lg font-mono">
                    AI Engineer @ Deriv
                  </h2>
                </div>
                <img 
                  src="/images/profile-photo.webp"
                  alt="Parth Kohale" 
                  className="w-16 h-16 lg:w-20 lg:h-20 rounded-full border-2 border-dev-accent shadow-glow flex-shrink-0 object-cover"
                  loading="eager"
                />
              </div>
              
              {/* Terminal-style path — doubles as the command palette trigger */}
              <button
                onClick={onOpenPalette}
                aria-label="Open command palette"
                className="group w-full flex items-center text-left font-mono text-sm lg:text-sm xl:text-base p-4 lg:p-4 bg-dev-bg-light rounded-lg border border-dev-border hover:border-dev-accent/60 transition-colors"
              >
                <span className="text-dev-accent-cyan mr-2 lg:mr-2">&gt;</span>
                <span className="font-bold text-black dark:text-white break-all">
                  cd {isTyping ? displayedPath : terminalPath}
                </span>
                <span className="text-dev-text terminal-cursor">█</span>
                <span className="ml-auto pl-2 text-xs text-dev-text-dimmed opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                  ⌘K
                </span>
              </button>
            </div>
            
            {/* About Me Section */}
            <div className="hidden lg:block mb-6 border border-dev-border/60 rounded-xl bg-white dark:bg-dev-bg-light/60 p-6 lg:p-5 xl:p-6 shadow-sm dark:shadow-none" style={{ marginBottom: window.innerWidth >= 1024 ? 'calc(var(--vertical-spacing) * 1.25)' : undefined, padding: window.innerWidth >= 1024 ? 'calc(var(--vertical-spacing) * 0.75)' : undefined }}>
              <div className="space-y-3 lg:space-y-3 text-dev-text-muted text-xs lg:text-sm leading-relaxed" style={{ display: window.innerWidth >= 1024 ? 'flex' : undefined, flexDirection: window.innerWidth >= 1024 ? 'column' : undefined, gap: window.innerWidth >= 1024 ? 'calc(var(--vertical-spacing) * 0.5)' : undefined }}>
                <p className="font-mono">
                  <span className="text-dev-accent-cyan">const</span> <span className="text-dev-text">developer</span> = &#123;
                </p>
                <p className="pl-4 font-mono">
                  <span className="text-dev-text-dimmed">name:</span> <span className="text-dev-success">"Parth Kohale"</span>,
                </p>
                <p className="pl-4 font-mono">
                  <span className="text-dev-text-dimmed">education:</span> <span className="text-dev-success">"CSE @ BITS Pilani"</span>,
                </p>
                <p className="pl-4 font-mono">
                  <span className="text-dev-text-dimmed">location:</span> <span className="text-dev-success">"Dubai, UAE"</span>,
                </p>
                <p className="pl-4 font-mono">
                  <span className="text-dev-text-dimmed">interests:</span> [<span className="text-dev-success">"AI/ML"</span>, <span className="text-dev-success">"GenAI"</span>, <span className="text-dev-success">"Data Science"</span>, <span className="text-dev-success">"Astrophysics"</span>, <span className="text-dev-success">"Guitar"</span>]
                </p>
                <p className="font-mono">&#125;;</p>
              </div>
              
              <div className="border-t border-dev-border pt-4 mt-4 space-y-2 text-sm">
                <a 
                  href="/PARTH_KOHALE_CV.pdf" 
                  download
                  className="flex items-center gap-2 text-dev-text-muted hover:text-dev-text transition-colors"
                >
                  <Download size={16} className="text-dev-accent" />
                  <span>Download Resume</span>
                </a>
              </div>
            </div>

            {/* Navigation Tabs */}
            <nav>
              <div className="flex flex-col space-y-2 lg:space-y-2" style={{ gap: window.innerWidth >= 1024 ? 'calc(var(--vertical-spacing) * 0.4)' : undefined }}>
                {[
                  { id: 'projects', label: 'Projects' },
                  { id: 'experience', label: 'Experience' },
                  { id: 'contact', label: 'Contact' },
                  { id: 'funhouse', label: 'Funhouse' },
                ].map((item) => {
                  const isActive = activeSection === item.id;
                  return (
                    <button
                      key={item.id}
                      onClick={() => handleNavigation(item.id)}
                      aria-current={isActive ? 'page' : undefined}
                      style={{ paddingTop: window.innerWidth >= 1024 ? 'calc(var(--vertical-spacing) * 0.6)' : undefined, paddingBottom: window.innerWidth >= 1024 ? 'calc(var(--vertical-spacing) * 0.6)' : undefined }}
                      className={`px-5 py-3 lg:px-5 font-mono text-base lg:text-base font-semibold text-left rounded-lg transition-all duration-300 flex items-center gap-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-dev-accent ${
                        isActive
                          ? 'text-dev-text'
                          : 'text-dev-text-muted hover:text-dev-text'
                      }`}
                    >
                      <span className={`text-dev-accent ${isActive ? '' : 'invisible'}`} aria-hidden="true">
                        &rsaquo;
                      </span>
                      {item.label}
                    </button>
                  );
                })}
              </div>
            </nav>
          </div>

          {/* Bottom Section - Social Icons and Theme Toggle */}
          <div className="flex-shrink-0 mt-auto pt-6 pb-6 lg:pb-0 lg:pt-6 border-t border-dev-border" style={{ paddingTop: window.innerWidth >= 1024 ? 'calc(var(--vertical-spacing) * 0.75)' : undefined }}>
            <div className="flex items-center gap-3 lg:gap-3">
              <a 
                href="https://github.com/mavericksxx" 
                target="_blank" 
                rel="noopener noreferrer"
                className="social-icon"
                aria-label="GitHub"
              >
                <Github size={20} />
              </a>
              <a 
                href="https://www.linkedin.com/in/parth-kohale-671534290" 
                target="_blank" 
                rel="noopener noreferrer"
                className="social-icon"
                aria-label="LinkedIn"
              >
                <Linkedin size={20} />
              </a>
              <a 
                href="https://instagram.com/parthh.kohale" 
                target="_blank" 
                rel="noopener noreferrer"
                className="social-icon"
                aria-label="Instagram"
              >
                <Instagram size={20} />
              </a>
              <a 
                href="mailto:parthkohale@gmail.com" 
                className="social-icon"
                aria-label="Email"
              >
                <Mail size={20} />
              </a>
              
              {/* Theme Toggle */}
              <button
                onClick={toggleTheme}
                className="social-icon ml-auto"
                aria-label="Toggle theme"
              >
                {isDarkMode ? <Moon size={20} /> : <Sun size={20} />}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar;